import { installLegacySettings } from '../api/client';
import type { BeatLineViteConfig } from '../api/types';

const VERSION = '2.17.2-c50-restore-working-native-auth';
const REMOTE_SITE = 'https://beatlinegame.com';

const defaultConfig: BeatLineViteConfig = {
  version: VERSION,
  debug: false,
  userLoggedIn: false,
  nativeApp: true,
  entryContext: 'app',
  remoteSiteUrl: REMOTE_SITE,
  appBootstrapUrl: `${REMOTE_SITE}/wp-json/beatline-vite/v1/app-bootstrap?native_app=1`,
  appBootstrapJsonpUrl: `${REMOTE_SITE}/?beatline_vite_app_bootstrap=1&native_app=1`,
  bootstrapStatus: { ok: false, method: 'default', required: true },
  beatlineSettings: {
    ajaxUrl: `${REMOTE_SITE}/wp-admin/admin-ajax.php`,
    nonce: '',
    scanUrl: `${REMOTE_SITE}/scanner/`,
    app: {
      entry: 'app',
      isAppShell: 1,
      isNativeApp: 1,
      isWebShell: 0,
      sameAccountMode: 'native_app_bootstrap',
      appUrl: 'https://localhost/',
      webUrl: `${REMOTE_SITE}/`,
      currentUrl: 'https://localhost/'
    },
    onlineAccess: { guests: 0, users: 1 },
    push: { enabled: 0, appVersion: VERSION },
    billing: { enabled: 0, subscriptionsEnabled: 0, subscriptionLevel: 0, subscriptionTiers: [], promoCodesEnabled: 0 },
    spotifyApp: { enabled: 0, nativeOnly: 1, defaultMode: 'preview' },
    onlineDefaults: { startTokens: 3, winCards: 10, disableBonusTokens: 1 },
    auth: {
      loggedIn: 0,
      displayName: '',
      loginUrl: `${REMOTE_SITE}/wp-login.php?redirect_to=${encodeURIComponent(`${REMOTE_SITE}/?beatline_native_auth_bridge=1`)}`,
      logoutUrl: `${REMOTE_SITE}/?beatline_native_logout=1&native_app=1`,
      accountUrl: `${REMOTE_SITE}/account/`,
      socialButtons: []
    },
    // Fail closed: if backend config cannot load, do not show QR mode from stale defaults.
    showQrMode: 0,
    firebase: { config: { apiKey: 'AIzaSyC-7WjXrybOw91N3bXX-kCIzmKtWDGORKM', authDomain: 'beatline-8b8cc.firebaseapp.com', projectId: 'beatline-8b8cc', storageBucket: 'beatline-8b8cc.firebasestorage.app', messagingSenderId: '975496522540', appId: '1:975496522540:web:094a41939270677d741a89', databaseURL: 'https://beatline-8b8cc-default-rtdb.europe-west1.firebasedatabase.app/' } }
  }
};

function isLikelyNativeLocalRuntime(): boolean {
  const host = window.location.hostname;
  return window.location.protocol === 'capacitor:'
    || window.location.protocol === 'file:'
    || host === 'localhost'
    || host === '127.0.0.1'
    || !!(window as any).Capacitor;
}

function mergeConfig(base: BeatLineViteConfig, incoming: Partial<BeatLineViteConfig>): BeatLineViteConfig {
  return {
    ...base,
    ...incoming,
    beatlineSettings: {
      ...base.beatlineSettings,
      ...(incoming.beatlineSettings || {}),
      app: { ...(base.beatlineSettings.app || {}), ...((incoming.beatlineSettings || {}).app || {}) },
      auth: { ...(base.beatlineSettings.auth || {}), ...((incoming.beatlineSettings || {}).auth || {}) },
      billing: { ...(base.beatlineSettings.billing || {}), ...((incoming.beatlineSettings || {}).billing || {}) },
      spotifyApp: { ...(base.beatlineSettings.spotifyApp || {}), ...((incoming.beatlineSettings || {}).spotifyApp || {}) },
      push: { ...(base.beatlineSettings.push || {}), ...((incoming.beatlineSettings || {}).push || {}) },
      onlineAccess: { ...(base.beatlineSettings.onlineAccess || {}), ...((incoming.beatlineSettings || {}).onlineAccess || {}) },
      onlineDefaults: { ...(base.beatlineSettings.onlineDefaults || {}), ...((incoming.beatlineSettings || {}).onlineDefaults || {}) },
    }
  };
}

function normalizeLocalAppConfig(config: BeatLineViteConfig, method: string, ok = true): BeatLineViteConfig {
  const normalized = { ...config };
  normalized.version = normalized.version || VERSION;
  normalized.nativeApp = true;
  normalized.entryContext = 'app';
  normalized.remoteSiteUrl = normalized.remoteSiteUrl || REMOTE_SITE;
  normalized.userLoggedIn = !!(normalized.userLoggedIn || normalized.beatlineSettings?.auth?.loggedIn);
  normalized.bootstrapStatus = { ok, method, required: true };

  normalized.beatlineSettings = {
    ...normalized.beatlineSettings,
    ajaxUrl: absoluteBackendUrl(normalized.beatlineSettings?.ajaxUrl, normalized.remoteSiteUrl),
    scanUrl: normalized.beatlineSettings?.scanUrl ? absoluteBackendUrl(normalized.beatlineSettings.scanUrl, normalized.remoteSiteUrl) : `${normalized.remoteSiteUrl.replace(/\/$/, '')}/scanner/`,
    showQrMode: normalized.beatlineSettings?.showQrMode ?? 0,
    app: {
      ...(normalized.beatlineSettings?.app || {}),
      entry: 'app',
      isAppShell: 1,
      isNativeApp: 1,
      isWebShell: 0,
      currentUrl: 'https://localhost/',
    }
  };

  return normalized;
}

function absoluteBackendUrl(url: string | undefined, remoteSiteUrl?: string): string {
  const value = String(url || '');
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  const base = String(remoteSiteUrl || REMOTE_SITE).replace(/\/$/, '');
  return base + (value.startsWith('/') ? value : '/' + value);
}

function extractBootstrapPayload(payload: any): Partial<BeatLineViteConfig> {
  return (payload && payload.success && payload.config) ? payload.config : (payload?.config || payload || {});
}

async function fetchBootstrapViaRest(config: BeatLineViteConfig): Promise<Partial<BeatLineViteConfig>> {
  const headers: Record<string, string> = { 'Cache-Control': 'no-cache', 'X-BeatLine-App': '1' };
  const restNonce = config.restNonce || (config.beatlineSettings as any)?.restNonce || '';
  if (restNonce) headers['X-WP-Nonce'] = String(restNonce);
  const bootstrapUrl = String(config.appBootstrapUrl || '');
  if (!bootstrapUrl) throw new Error('REST bootstrap URL ontbreekt');
  const url = bootstrapUrl + (bootstrapUrl.includes('?') ? '&' : '?') + '_=' + encodeURIComponent(String(Date.now()));
  const response = await fetch(url, { credentials: 'include', headers, cache: 'no-store', mode: 'cors' });
  if (!response.ok) throw new Error(`App bootstrap REST mislukt: HTTP ${response.status}`);
  const payload = await response.json();
  return extractBootstrapPayload(payload);
}

async function fetchBootstrapViaJsonp(config: BeatLineViteConfig): Promise<Partial<BeatLineViteConfig>> {
  const jsonpBase = String(config.appBootstrapJsonpUrl || `${(config.remoteSiteUrl || REMOTE_SITE).replace(/\/$/, '')}/?beatline_vite_app_bootstrap=1&native_app=1`);
  const callbackName = `__beatlineAppBootstrapC50_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const url = jsonpBase
    + (jsonpBase.includes('?') ? '&' : '?')
    + 'callback=' + encodeURIComponent(callbackName)
    + '&_=' + encodeURIComponent(String(Date.now()));

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    let settled = false;
    const cleanup = () => {
      try { delete (window as any)[callbackName]; } catch (_) { (window as any)[callbackName] = undefined; }
      try { script.remove(); } catch (_) {}
    };
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('App bootstrap JSONP timeout'));
    }, 9000);

    (window as any)[callbackName] = (payload: any) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      resolve(extractBootstrapPayload(payload));
    };

    script.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      reject(new Error('App bootstrap JSONP script kon niet laden'));
    };
    script.async = true;
    script.src = url;
    document.head.appendChild(script);
  });
}

export async function hydrateAppConfig(config: BeatLineViteConfig): Promise<BeatLineViteConfig> {
  const hasPageNonce = !!config.beatlineSettings?.nonce;
  const localNativeRuntime = isLikelyNativeLocalRuntime();
  const shouldHydrate = !!config.appBootstrapUrl && (localNativeRuntime || !hasPageNonce);
  if (!shouldHydrate) return normalizeLocalAppConfig(config, 'page-config', true);

  const errors: string[] = [];
  try {
    const incoming = await fetchBootstrapViaRest(config);
    const hydrated = mergeConfig(config, incoming || {});
    // Preserve an existing same-page nonce only in remote shortcode mode.
    if (!localNativeRuntime && config.beatlineSettings?.nonce) {
      hydrated.beatlineSettings.nonce = config.beatlineSettings.nonce;
      (hydrated.beatlineSettings as any).restNonce = (config.beatlineSettings as any).restNonce || (incoming?.beatlineSettings || {}).restNonce;
    }
    return normalizeLocalAppConfig(hydrated, 'rest', true);
  } catch (error: any) {
    errors.push(String(error?.message || error));
  }

  try {
    const incoming = await fetchBootstrapViaJsonp(config);
    const hydrated = mergeConfig(config, incoming || {});
    return normalizeLocalAppConfig(hydrated, 'jsonp', true);
  } catch (error: any) {
    errors.push(String(error?.message || error));
  }

  console.warn('[BeatLine Vite C50] app bootstrap fallback active', errors);
  const fallback = normalizeLocalAppConfig(config, 'fallback', false);
  fallback.bootstrapStatus = { ok: false, method: 'fallback', required: true, error: errors.join(' | ') };
  // Fail closed: without backend bootstrap we cannot safely run playlists/audio.
  fallback.beatlineSettings.showQrMode = 0;
  return fallback;
}

export function getConfig(): BeatLineViteConfig {
  const incoming = window.beatlineViteConfig || {} as BeatLineViteConfig;
  const merged = mergeConfig(defaultConfig, incoming);
  if (isLikelyNativeLocalRuntime()) {
    merged.nativeApp = true;
    merged.entryContext = 'app';
    merged.userLoggedIn = !!(merged.userLoggedIn || merged.beatlineSettings?.auth?.loggedIn);
  }
  return merged;
}

export function installConfig(config: BeatLineViteConfig): void {
  window.BEATLINE_VITE_DEBUG = !!config.debug;
  window.beatlineViteConfig = config;
  installLegacySettings(config.beatlineSettings);
}

export function patchShellDataset(config: BeatLineViteConfig): void {
  const app = document.getElementById('beatline-app');
  if (!app) return;
  app.dataset.userLoggedIn = config.userLoggedIn ? '1' : '0';
  app.dataset.nativeApp = config.nativeApp ? '1' : '0';
  app.dataset.entryContext = config.entryContext || 'web';
  app.dataset.appShell = config.entryContext === 'app' ? '1' : '0';
  if (config.beatlineSettings?.app) {
    const appCfg = config.beatlineSettings.app as Record<string, any>;
    if (appCfg.appUrl) app.dataset.appUrl = String(appCfg.appUrl);
    if (appCfg.webUrl) app.dataset.webUrl = String(appCfg.webUrl);
  }
  if (config.nativeApp || config.entryContext === 'app') {
    document.documentElement.classList.add('beatline-native-app');
    document.body?.classList.add('beatline-native-app');
    ['btn-fullscreen', 'btn-fullscreen-start'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) { el.style.display = 'none'; el.setAttribute('hidden', 'hidden'); }
    });
  }
}
